import type { ChartConfig, OptionSchema } from '@chartforge/shared-types';

/** Valid {@link ChartConfig} buckets. Option schemas must target one of these. */
export const CONFIG_GROUPS = ['appearance', 'layout', 'axes', 'labels', 'data'] as const;

export type ConfigGroup = (typeof CONFIG_GROUPS)[number];

export class ConfigValidationError extends Error {
  constructor(
    message: string,
    readonly optionId: string
  ) {
    super(message);
    this.name = 'ConfigValidationError';
  }
}

function asGroup(group: string, optionId: string): ConfigGroup {
  if ((CONFIG_GROUPS as readonly string[]).includes(group)) {
    return group as ConfigGroup;
  }
  throw new ConfigValidationError(
    `Option "${optionId}" targets unknown config group "${group}"`,
    optionId
  );
}

/** Validate a value against an option schema. Returns a list of problems (empty = valid). */
export function validateOptionValue(schema: OptionSchema, value: unknown): string[] {
  const errors: string[] = [];
  const { validation } = schema;
  if (!validation) {
    return errors;
  }
  if (typeof value === 'number') {
    if (validation.min !== undefined && value < validation.min) {
      errors.push(`Value ${value} is below minimum ${validation.min}`);
    }
    if (validation.max !== undefined && value > validation.max) {
      errors.push(`Value ${value} is above maximum ${validation.max}`);
    }
  }
  if (validation.options !== undefined) {
    if (typeof value !== 'string' || !validation.options.includes(value)) {
      errors.push(
        `Value ${JSON.stringify(value)} is not one of [${validation.options.join(', ')}]`
      );
    }
  }
  return errors;
}

/** Build the default {@link ChartConfig} for a set of option schemas. */
export function createDefaultConfig(schemas: OptionSchema[]): ChartConfig {
  const config: ChartConfig = {
    appearance: {},
    layout: {},
    axes: {},
    labels: {},
    data: {}
  };
  for (const schema of schemas) {
    applyOption(config, schema, schema.defaultValue);
  }
  return config;
}

/** Read the current value of an option (falls back to its schema default). */
export function getOptionValue(config: ChartConfig, schema: OptionSchema): unknown {
  const bucket = config[asGroup(schema.group, schema.id)];
  const value = bucket[schema.id];
  return value === undefined ? schema.defaultValue : value;
}

/**
 * Immutably update one option, returning a new config. Validates against the
 * schema and delegates to `schema.apply` when present, otherwise writes to
 * `config[group][id]`.
 */
export function updateConfig(
  config: ChartConfig,
  schema: OptionSchema,
  value: unknown
): ChartConfig {
  const errors = validateOptionValue(schema, value);
  if (errors.length > 0) {
    throw new ConfigValidationError(errors.join('; '), schema.id);
  }
  const next = cloneConfig(config);
  applyOption(next, schema, value);
  return next;
}

export function cloneConfig(config: ChartConfig): ChartConfig {
  return {
    appearance: { ...config.appearance },
    layout: { ...config.layout },
    axes: { ...config.axes },
    labels: { ...config.labels },
    data: { ...config.data }
  };
}

function applyOption(config: ChartConfig, schema: OptionSchema, value: unknown): void {
  if (schema.apply) {
    schema.apply(config, value);
    return;
  }
  config[asGroup(schema.group, schema.id)][schema.id] = value;
}
